/**
 * Decision Panel — reason codes + note textarea + Approve/Hold/Reject buttons +
 * Un-hold button (mode='held') + L1-pending banner + per-item override warning.
 * Also owns the "override per-item decisions?" confirmation dialog.
 */
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { CheckCircle2, XCircle, PauseCircle, RotateCcw, Wand2, AlertTriangle } from "lucide-react";

const REASON_CODES = [
  { code: "price_spike", label: "Price spike" },
  { code: "cheaper_vendor", label: "Cheaper vendor available" },
  { code: "wrong_spec", label: "Wrong specifications" },
  { code: "budget_exceeded", label: "Budget exceeded" },
  { code: "duplicate_pr", label: "Duplicate PR" },
  { code: "need_clarification", label: "Need clarification" },
];

export default function DecisionPanel({
  pr, mode, isAuth, acting,
  note, setNote,
  reasonCode, setReasonCode,
  hasItemDecisions, itemsWithDecision,
  pendingPRAction, setPendingPRAction,
  onDecide, onUnhold,
}) {
  const handlePRBtn = (action) => {
    if (hasItemDecisions) setPendingPRAction(action);
    else onDecide(action);
  };
  const l1Pending = !isAuth && pr && pr.l1_done === false;

  return (
    <div>
      <div className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-2">Decision</div>
      <div className="mb-2">
        <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1.5">
          Reason (optional, used for Hold/Reject reports)
        </div>
        <div className="flex flex-wrap gap-1.5">
          {REASON_CODES.map((r) => (
            <button
              key={r.code}
              type="button"
              data-testid={`reason-${r.code}`}
              onClick={() => setReasonCode(reasonCode === r.code ? "" : r.code)}
              className={`px-2 py-1 text-[11px] font-semibold rounded-full border transition-colors ${
                reasonCode === r.code
                  ? "bg-cyan-700 text-white border-purple-900"
                  : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      <Textarea
        placeholder="Additional note (optional)..."
        value={note}
        onChange={(e) => setNote(e.target.value)}
        rows={2}
        data-testid="decision-note"
        className="mb-3"
      />

      {l1Pending && (
        <div
          data-testid="l1-pending-banner"
          className="text-[11px] text-amber-900 bg-amber-50 border border-amber-300 rounded px-2 py-1.5 mb-2 flex items-start gap-1.5"
        >
          <AlertTriangle size={12} className="mt-0.5 flex-shrink-0" />
          <span>
            Approval blocked — Layer&nbsp;1 authorisation pending from <b>{pr.auth_by || "L1 authoriser"}</b>.
            Ask them to authorise this PR first.
          </span>
        </div>
      )}

      <div className="grid grid-cols-3 gap-2">
        <Button
          onClick={() => handlePRBtn("approve")}
          disabled={acting || l1Pending}
          data-testid="btn-approve"
          className="bg-emerald-600 hover:bg-emerald-700 text-white"
          title={l1Pending ? "Pending L1 authorisation" : undefined}
        >
          <CheckCircle2 size={16} className="mr-1" />{isAuth ? "Authorise" : "Approve"}
        </Button>
        <Button
          onClick={() => handlePRBtn("hold")}
          disabled={acting}
          data-testid="btn-hold"
          variant="outline"
          className="border-yellow-400 text-yellow-900 hover:bg-yellow-50"
        >
          <PauseCircle size={16} className="mr-1" />Hold
        </Button>
        <Button
          onClick={() => handlePRBtn("reject")}
          disabled={acting}
          data-testid="btn-reject"
          variant="destructive"
        >
          <XCircle size={16} className="mr-1" />Reject
        </Button>
      </div>

      {mode === "held" && (
        <Button
          onClick={onUnhold}
          disabled={acting}
          data-testid="btn-unhold"
          className="w-full mt-2 bg-cyan-700 hover:bg-cyan-600 text-white"
        >
          <RotateCcw size={16} className="mr-1" />Un-hold &amp; re-queue
        </Button>
      )}

      {hasItemDecisions && (
        <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5 mt-2 flex items-start gap-1">
          <Wand2 size={12} className="mt-0.5 flex-shrink-0" />
          <span>
            {itemsWithDecision.length} item-level decision{itemsWithDecision.length > 1 ? "s" : ""} set.
            Use <b>Finalize</b> above to apply them, or a PR-level action below to override all items.
          </span>
        </p>
      )}

      <p className="text-[11px] text-slate-500 mt-2">
        Decision is written to PoRequest (ReqStatus, App_by, App_date) and PoRequesti.AIResult in MS SQL.
      </p>

      {/* Override-per-item confirmation dialog */}
      <AlertDialog open={!!pendingPRAction} onOpenChange={(v) => !v && setPendingPRAction(null)}>
        <AlertDialogContent data-testid="override-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Override per-item decisions?</AlertDialogTitle>
            <AlertDialogDescription>
              You have {itemsWithDecision.length} line item{itemsWithDecision.length > 1 ? "s" : ""} with
              manual decisions ({itemsWithDecision.map((x) => x.decision.action).join(", ")}).
              Marking the whole PR as <b>{pendingPRAction?.toUpperCase()}</b> will overwrite every
              line item&apos;s AIResult with the PR-level decision.
              <br /><br />
              Tip: use the purple <b>Finalize</b> button above to roll up your per-item decisions
              instead of overriding them.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="override-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="override-confirm"
              onClick={() => {
                const a = pendingPRAction;
                setPendingPRAction(null);
                onDecide(a);
              }}
              className="bg-rose-600 hover:bg-rose-700 text-white"
            >
              Yes, override all items
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
